import { ArrowLeft, TrendingUp, AlertTriangle, BarChart3, FileText } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

interface MarketIntelligencePaneProps {
  onBack: () => void;
}

function RateChip({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border bg-muted/30 px-3 py-2 min-w-[100px]">
      <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className="text-[18px] font-semibold text-foreground leading-none tabular-nums">{value}</span>
      {sub && <span className="text-[10px] text-muted-foreground">{sub}</span>}
    </div>
  );
}

function TenderChip({ label, value }: { label: string; value: string }) {
  const num = parseFloat(value);
  const color = num >= 20 ? "text-destructive" : num >= 10 ? "text-warning" : "text-success";
  return (
    <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-1.5">
      <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
      <span className={`text-[14px] font-semibold tabular-nums ${color}`}>{value}%</span>
    </div>
  );
}

export function MarketIntelligencePane({ onBack }: MarketIntelligencePaneProps) {
  return (
    <div className="flex flex-col gap-0 h-full rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-card)" }}>
      {/* Header */}
      <div className="flex items-center gap-2 border-b px-3 py-2.5 bg-muted/20">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-[11px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to My Loads
        </button>
        <span className="text-[10px] text-muted-foreground">|</span>
        <h4 className="text-caption font-bold text-foreground">Market Intelligence</h4>
        <Badge variant="secondary" className="ml-auto text-[9px] px-1.5 py-0">
          Week of June 1st, 2026
        </Badge>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-3 py-2">
        <Accordion type="multiple" defaultValue={["rates"]} className="w-full">

          {/* Section 1: Rate Data */}
          <AccordionItem value="rates" className="border-b-border/50">
            <AccordionTrigger className="py-2.5 text-[12px] font-bold hover:no-underline gap-2">
              <span className="flex items-center gap-1.5">
                <TrendingUp className="h-3.5 w-3.5 text-primary" />
                Rate Data
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-0">
              <div className="space-y-3">
                <div className="rounded-md border bg-muted/20 px-3 py-2">
                  <p className="text-[11px] text-foreground">
                    <span className="font-semibold">National Broker Spot Rate:</span> $2.28/mi · FSC $0.73 · Combined <span className="font-bold text-primary">$3.01/mi</span>
                  </p>
                  <p className="text-[11px] text-foreground mt-1">
                    <span className="font-semibold">National Contract Rate:</span> $2.31/mi <span className="text-muted-foreground">(without fuel)</span>
                  </p>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <RateChip label="Van" value="$2.85" sub="/mi" />
                  <RateChip label="Flatbed" value="$3.64" sub="/mi" />
                  <RateChip label="Reefer" value="$3.34" sub="/mi" />
                </div>
                <p className="text-[10px] italic text-muted-foreground">
                  Spot rates may continue to rise as capacity exits the market.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Section 2: Capacity Trends */}
          <AccordionItem value="capacity" className="border-b-border/50">
            <AccordionTrigger className="py-2.5 text-[12px] font-bold hover:no-underline gap-2">
              <span className="flex items-center gap-1.5">
                <BarChart3 className="h-3.5 w-3.5 text-warning" />
                Capacity Trends — Tender Rejections
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-0">
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <TenderChip label="OTRI (Overall)" value="16.29" />
                  <TenderChip label="VOTRI (Van)" value="15.21" />
                  <TenderChip label="FOTRI (Flatbed)" value="46.42" />
                  <TenderChip label="ROTRI (Reefer)" value="16.50" />
                </div>
                <p className="text-[10px] italic text-muted-foreground">
                  Tender rejections indicate capacity conditions. Falling rejection rates can signal weaker carrier pricing power, while rising rejection rates can signal tightening capacity and stronger carrier leverage.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Section 3: Market Data */}
          <AccordionItem value="market" className="border-b-border/50">
            <AccordionTrigger className="py-2.5 text-[12px] font-bold hover:no-underline gap-2">
              <span className="flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                Market Data &amp; Regulatory Updates
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-0">
              <ul className="space-y-1.5 text-[11px] text-foreground list-disc pl-4">
                <li>Truckload spot rates remain <strong>elevated</strong>, holding well above pre-pandemic norms even as the market digests recent highs set during the COVID era.</li>
                <li><strong>2026 CVSA Roadcheck</strong> ran last week: <strong>38,926 inspections</strong> and <strong>13,273 out-of-service orders</strong> across <strong>25,008 carriers</strong>. Last year's Roadcheck ran 56,178 inspections and produced 10,148 OOS orders — <strong>31% fewer inspections, 30% more OOS violations</strong>.</li>
                <li>The <strong>Supreme Court's broker liability ruling</strong> sent transportation stocks surging, as investors expect consolidation from <strong>20–30% of smaller brokers</strong> exiting the industry.</li>
                <li>The <strong>S&amp;P Global U.S. Manufacturing PMI</strong> rose to <strong>55.3 in May 2026</strong>, beating expectations — the strongest manufacturing expansion since May 2022, with output growing at the fastest pace in over four years and job creation at its highest since June 2025.</li>
                <li>The <strong>FMCSA</strong> has pulled <strong>79 electronic logging devices</strong> from the approved registry since January 2025, with <strong>12 more added this week</strong>. Carriers running revoked ELDs face immediate out-of-service orders.</li>
                <li>Industry chatter confirmed truckload spot rates remain at multi-year highs. One construction contractor reported a delivery that cost <strong>$1,800 in December</strong> ran <strong>$6,450 last week</strong>.</li>
                <li>The <strong>FMCSA launched Motus</strong>, a new registration system for truck drivers, motor coach operators, and other motor carriers. Motus replaces a decades-old system and uses <strong>biometrics and modern data analytics</strong> to verify authority applicants.</li>
                <li>CVSA's surprise single-day <strong>brake inspection</strong> ran April 14th: <strong>574 Commercial Motor Vehicles (14.3%)</strong> placed out of service due to brake-related violations.</li>
                <li>An auditor general report from <strong>Ontario, Canada</strong> concluded authorities failed to effectively monitor commercial truck driver training, inspections, and licensing, uncovering widespread compliance gaps industry groups warn have jeopardized highway safety for years.</li>
                <li>
                  <strong>Safety Enforcement Events:</strong>
                  <ul className="list-disc pl-4 mt-0.5">
                    <li>Operation Safe Driver Week: July 12–18</li>
                  </ul>
                  <span className="text-muted-foreground text-[10px]">These events typically reduce available capacity as drivers temporarily exit the market. It's never too early to inform your customers.</span>
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>

          {/* Section 4: Fraud Alerts & Supply Chain Risks */}
          <AccordionItem value="fraud" className="border-b-border/50">
            <AccordionTrigger className="py-2.5 text-[12px] font-bold hover:no-underline gap-2">
              <span className="flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                Fraud Alerts &amp; Supply Chain Risks
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-0">
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <RateChip label="Incidents" value="767" sub="Q1 2026" />
                  <RateChip label="Losses" value="$131.58M" sub="Est. total" />
                  <RateChip label="Top 3 States" value="54.3%" sub="of Q1 theft" />
                </div>
                <ul className="space-y-1.5 text-[11px] text-foreground list-disc pl-4">
                  <li>Top 3 targeted states: <strong>California</strong>, <strong>Texas</strong>, and <strong>New Jersey</strong> — together representing 54.3% of all reported Q1 2026 theft activity.</li>
                  <li>Top targeted location types: <strong>warehouses / distribution centers</strong> and <strong>truck stops</strong>.</li>
                  <li>Top stolen commodity types: <strong>food and beverage</strong> and <strong>household goods</strong>.</li>
                </ul>
                <ul className="space-y-1.5 text-[11px] text-foreground list-disc pl-4">
                  <li>Theft risk remains elevated around handoff points, storage points, and high-volume freight corridors.</li>
                  <li>Extra verification and dispatch controls matter most on high-theft lanes.</li>
                  <li>Food and household freight should be treated as higher-risk categories.</li>
                  <li>Truck stops and warehouse / distribution environments remain key exposure points.</li>
                  <li>Customers and carriers should tighten identity verification, pickup controls, routing discipline, and exception monitoring.</li>
                </ul>
                <p className="text-[10px] italic text-muted-foreground">
                  Fraud and supply-chain risk are now a parallel market story and should be part of routine broker–customer conversations.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Section 5: Market Summary */}
          <AccordionItem value="summary" className="border-b-0">
            <AccordionTrigger className="py-2.5 text-[12px] font-bold hover:no-underline gap-2">
              <span className="flex items-center gap-1.5">
                <FileText className="h-3.5 w-3.5 text-accent" />
                Market Summary
              </span>
            </AccordionTrigger>
            <AccordionContent className="pb-3 pt-0">
              <div className="text-[11px] text-foreground space-y-2 leading-relaxed">
                <p>The domestic truckload market remains <strong>structurally tight</strong>, even as macro uncertainty clouds near-term visibility. Mid-April brought greater stability to dry van spot rates, which for now have leveled off, while tender rejection rates settled slightly. Year-over-year comparisons, however, continue to underscore carriers' <strong>strengthened pricing power</strong> amid persistent capacity constraints.</p>
                <p>The freight market is transitioning from recovery into a more <strong>durable tightening cycle</strong>. The start of the produce season is a key development for both dry van and reefer segments soaking up capacity, while the <strong>Flatbed market is as hot as it has ever been</strong> with the ongoing construction of data centers throughout the United States pushing spot rates to the highest levels seen in years.</p>
                <p>From geopolitical disruptions in the Middle East to upcoming enforcement events and summer demand patterns, external factors are reinforcing supply-side discipline. <strong>Regulatory and legal risks loom large</strong> as unknowns that will absolutely affect the market, including FMCSA compliance crackdowns on capacity and the recent Supreme Court ruling on freight broker liability.</p>
                <p className="text-muted-foreground italic">Overall, the market continues to face <strong>supply-side constraints</strong> leading to upward pressure on rates in the coming months, even if demand growth remains modest.</p>
              </div>
            </AccordionContent>
          </AccordionItem>

        </Accordion>
      </div>
    </div>
  );
}
